import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';

interface EmailItem {
  id: string;
  toEmail: string;
  fromEmail: string;
  subject: string;
  kind: string;
  status: string;
  resendId: string | null;
  errorMessage: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
  complainedAt: string | null;
  createdAt: string;
  cliente: {
    id: string;
    primeiroNome: string;
    telefone: string;
    nomeCompleto: string | null;
  } | null;
  compra: {
    id: string;
    produto: string;
    valorCentavos: number;
    status: string;
  } | null;
}

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendente' },
  { value: 'sent', label: 'Enviado' },
  { value: 'delivered', label: 'Entregue' },
  { value: 'opened', label: 'Aberto' },
  { value: 'clicked', label: 'Clicado' },
  { value: 'bounced', label: 'Bounce' },
  { value: 'complained', label: 'Spam' },
  { value: 'failed', label: 'Falha' },
];

const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-sara-bg-muted text-sara-text-muted',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  opened: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  clicked: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  bounced: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  complained: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  delivery_delayed: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

export function EmailsPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data, isLoading, isError, refetch, isFetching } = useQuery<{
    items: EmailItem[];
    total: number;
  }>({
    queryKey: ['emails', statusFilter],
    queryFn: () =>
      request<{ items: EmailItem[]; total: number }>(
        `/admin/emails?limit=200${statusFilter ? `&status=${statusFilter}` : ''}`,
      ),
    refetchInterval: 30_000,
  });

  return (
    <section aria-labelledby="emails-heading" className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 id="emails-heading" className="h1">
            Emails enviados
          </h1>
          <p className="muted text-sm mt-1">
            Boas-vindas via Resend. Atualiza automaticamente a cada 30s.
          </p>
        </div>
        <button onClick={() => refetch()} className="btn-ghost text-sm" disabled={isFetching}>
          {isFetching ? 'Atualizando…' : '↻ Atualizar'}
        </button>
      </header>

      <div className="card space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              onClick={() => setStatusFilter(f.value)}
              className={`text-xs px-3 py-1.5 rounded-md border ${
                statusFilter === f.value
                  ? 'border-sara-accent bg-sara-accent/10 text-sara-accent'
                  : 'border-sara-border text-sara-text-muted hover:text-sara-text'
              }`}
            >
              {f.label}
            </button>
          ))}
          {data && (
            <span className="text-xs text-sara-text-subtle ml-auto">{data.total} no total</span>
          )}
        </div>

        {isLoading && <p className="text-sara-text-muted text-sm">carregando…</p>}
        {isError && (
          <p role="alert" className="text-sara-danger text-sm">
            erro carregando emails
          </p>
        )}

        {data && data.items.length === 0 && (
          <p className="text-sara-text-muted text-sm py-8 text-center">
            Nenhum email {statusFilter ? `com status "${statusFilter}"` : 'enviado'} ainda.
          </p>
        )}

        {data && data.items.length > 0 && (
          <div className="overflow-x-auto -mx-3">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-sara-text-subtle border-b border-sara-border">
                <tr>
                  <th className="px-3 py-2">Cliente</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Compra</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Aberto</th>
                  <th className="px-3 py-2">Clicado</th>
                  <th className="px-3 py-2">Enviado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sara-border">
                {data.items.map((item) => (
                  <tr key={item.id} className="hover:bg-sara-bg-muted/40">
                    <td className="px-3 py-2 align-top">
                      {item.cliente ? (
                        <div>
                          <div className="font-medium text-sara-text">
                            {item.cliente.nomeCompleto ?? item.cliente.primeiroNome}
                          </div>
                          <div className="text-xs text-sara-text-subtle">
                            {item.cliente.telefone}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sara-text-subtle">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-sara-text-muted">{item.toEmail}</td>
                    <td className="px-3 py-2 align-top">
                      {item.compra ? (
                        <div>
                          <div className="text-sara-text">{item.compra.produto}</div>
                          <div className="text-xs text-sara-text-subtle">
                            R$ {(item.compra.valorCentavos / 100).toFixed(2)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sara-text-subtle">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span
                        className={`inline-block text-xs px-2 py-0.5 rounded ${
                          STATUS_COLOR[item.status] ?? 'bg-sara-bg-muted text-sara-text-muted'
                        }`}
                      >
                        {item.status}
                      </span>
                      {item.errorMessage && (
                        <div
                          className="text-xs text-sara-danger mt-1 max-w-[200px] truncate"
                          title={item.errorMessage}
                        >
                          {item.errorMessage}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-sara-text-muted">
                      {item.openedAt ? formatRelative(item.openedAt) : '—'}
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-sara-text-muted">
                      {item.clickedAt ? formatRelative(item.clickedAt) : '—'}
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-sara-text-muted">
                      {item.sentAt ? formatRelative(item.sentAt) : formatRelative(item.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min}min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days}d`;
  return d.toLocaleDateString('pt-BR');
}
